import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { BRAND, CONCEPTS, DISCLAIMER } from "@shared/copy";
import { api } from "../lib/api";
import type { OneNetworkHealth, SourceHealth } from "@shared/types";
import { StatusPill } from "../components/Status";

export function About() {
  return (
    <section className="page-hero">
      <div className="wrap">
        <p className="kicker">About</p>
        <h1>Understand the system.</h1>
        <p className="lede">{BRAND.tagline}</p>
        <p>
          Floodplain.com.au = understand a place. Floodplains.net = understand the system. OneNetwork = shared
          intelligence infrastructure beneath both.
        </p>
        <p>{DISCLAIMER}</p>
      </div>
    </section>
  );
}

export function Methodology() {
  return (
    <section className="page-hero">
      <div className="wrap">
        <p className="kicker">Methodology <span className="pill pill-dim">v1 / draft</span></p>
        <h1>How Floodplains treats knowledge.</h1>
        <p className="lede">{BRAND.neverClaim}</p>
        <article className="card">
          <h3>Epistemic states</h3>
          <p>Every extracted item carries a state: authoritative, public technical, AI extracted — unverified, human verified, professional verified, inferred, conflicting, superseded, unknown, or DEMO / SYNTHETIC.</p>
        </article>
        <article className="card" style={{ marginTop: 12 }}>
          <h3>What we measure</h3>
          <p>We do not score models. We show source completeness, documentation completeness, calibration evidence, validation evidence, terrain currency, hydrology currency, assumption transparency, extrapolation exposure, observed-event performance, and unresolved conflicts — each with its evidence basis.</p>
        </article>
        <article className="card" style={{ marginTop: 12 }}>
          <h3>Confidence</h3>
          <p>Confidence is multidimensional and visually separate from any status label. Calibration fit is not certainty.</p>
        </article>
        <h2 style={{ marginTop: 48 }}>Concepts</h2>
        <div className="results">
          {CONCEPTS.map((c) => (
            <Link className="result" key={c.slug} to={`/concepts/${c.slug}`}>
              <h3>{c.title}</h3>
              <p className="muted">{c.dek}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export function DataPage() {
  const [health, setHealth] = useState<{ onenetwork: OneNetworkHealth } | null>(null);
  const [sources, setSources] = useState<SourceHealth[]>([]);
  useEffect(() => {
    api<{ onenetwork: OneNetworkHealth }>("/api/health").then(setHealth).catch(() => setHealth(null));
    api<{ sources: SourceHealth[] }>("/api/sources").then((r) => setSources(r.sources)).catch(() => setSources([]));
  }, []);
  const on = health?.onenetwork;
  return (
    <section className="page-hero">
      <div className="wrap">
        <p className="kicker">Data</p>
        <h1>Adapters, provenance, source health.</h1>
        <article className="card">
          <h3>OneNetwork adapter</h3>
          <p>Enabled: {on?.enabled ? "yes" : "no"} · Reachable: {on?.ok ? "yes" : "no"}</p>
          <p className="muted">{on?.message}</p>
        </article>
        <table className="table" style={{ marginTop: 24 }}>
          <thead>
            <tr><th>Source</th><th>Provider</th><th>Status</th><th>Last ingested</th><th>Coverage</th></tr>
          </thead>
          <tbody>
            {sources.map((s) => (
              <tr key={s.id}>
                <td>{s.name}</td>
                <td>{s.organisation}</td>
                <td><StatusPill status={s.status} /></td>
                <td className="font-data">{s.lastSuccessAt ?? "—"}</td>
                <td>{s.geography}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="muted small" style={{ marginTop: 16 }}>
          LIVE uses this app&apos;s accent. MOCK is amber and untrustworthy as a feed. REQUIRES_VERIFICATION is grey — pending, not a judgement.
        </p>
      </div>
    </section>
  );
}

export function Concept() {
  const { slug } = useParams();
  const c = CONCEPTS.find((x) => x.slug === slug);
  if (!c) return <section className="page-hero"><div className="wrap"><h1>Unknown concept</h1></div></section>;
  return (
    <section className="page-hero">
      <div className="wrap">
        <p className="kicker">Concept</p>
        <h1>{c.title}</h1>
        <p className="lede">{c.dek}</p>
        <p className="muted">{BRAND.neverClaim}</p>
      </div>
    </section>
  );
}
